/**
 * The pure half of the cross-job rate comparison: which rows disagree, which
 * rows a search is asking for, and what a cell reads as.
 *
 * Extracted from the modal because every one of these is a judgement a PM will
 * act on — "these two jobs carry Calloway at different rates" is the whole
 * reason the screen exists — and none of it is testable through a component in
 * a Node test environment.
 */
import type { RateComparisonJob, RateComparisonKind, RateComparisonRow } from '@coffman-tm/shared';
import { formatMoneyCents } from './format';

/**
 * The job columns the table should render, in the server's order.
 *
 * `hidden` is per-open state, never persisted: unticking a job is a way of
 * reading THIS table, not a preference about the screen. Order comes from the
 * response rather than the set, so ticking a job back on puts its column where
 * it was instead of on the end.
 */
export function visibleJobs(
  jobs: ReadonlyArray<RateComparisonJob>,
  hidden: ReadonlySet<string>,
): RateComparisonJob[] {
  return jobs.filter((job) => !hidden.has(job.project_id));
}

/**
 * The rates a row is read by: the job's own rate per column, one per job.
 *
 * One accessor so the table and `differsSomewhere` can never end up reading
 * different numbers for the same row.
 */
export function ratesFor(row: RateComparisonRow): Record<string, number | null> {
  return row.rates;
}

/**
 * True when the jobs that DO carry this row don't agree on its rate.
 *
 * Absences are skipped rather than counted as a difference: a person who is on
 * one job has nothing to disagree with, and a row present on exactly one job
 * is not a finding. That is also why a row nobody carries a rate on is false —
 * there is nothing to compare, so there is nothing to report.
 *
 * `jobIds` narrows the comparison to the columns on screen. Left out, every
 * job in the row is compared. It matters because the answer has to match what
 * the reader can see: with two jobs unticked, a row whose only disagreement
 * was between those two is not a difference in the table being read.
 */
export function differsSomewhere(row: RateComparisonRow, jobIds?: ReadonlyArray<string>): boolean {
  const rates = ratesFor(row);
  const cells = jobIds === undefined ? Object.values(rates) : jobIds.map((id) => rates[id] ?? null);
  const present = cells.filter((cents): cents is number => cents !== null);
  return present.some((cents) => cents !== present[0]);
}

export interface RateComparisonFilter {
  query: string;
  onlyDifferences: boolean;
  /**
   * The job columns on screen. Omitted means all of them — the differences
   * toggle answers about what is visible, not about data behind an unticked
   * column.
   */
  jobIds?: ReadonlyArray<string>;
}

/** Whether any of the ticked jobs carries this row at all. */
function isCarried(row: RateComparisonRow, jobIds?: ReadonlyArray<string>): boolean {
  return jobIds === undefined || jobIds.some((id) => row.rates[id] != null);
}

/**
 * The rows at least one ticked job carries — everything the table could show
 * before the search and the toggle narrow it further.
 *
 * Exists so the counts line's denominator is the same set the table is drawn
 * from: with two of eight jobs ticked, "175 employees" would be counting six
 * rosters the reader cannot see. With every job ticked (and with `jobIds`
 * omitted) it is the whole response — the API never sends a row no job
 * carries.
 */
export function carriedRows(
  rows: ReadonlyArray<RateComparisonRow>,
  jobIds?: ReadonlyArray<string>,
): RateComparisonRow[] {
  return rows.filter((row) => isCarried(row, jobIds));
}

/**
 * The rows the table should render. Search matches the key or the label — the
 * id is what the foreman's PDF prints, so it is as likely a search term as the
 * name.
 *
 * A row no ticked job carries is dropped before any of that: every cell of it
 * would be a dash, which reads as a roster entry nobody has a rate for rather
 * than as a person on a job that is off screen. Searching cannot bring one
 * back; a row the ticked jobs DO carry stays findable by name as always.
 */
export function filterRows(
  rows: ReadonlyArray<RateComparisonRow>,
  { query, onlyDifferences, jobIds }: RateComparisonFilter,
): RateComparisonRow[] {
  const q = query.trim().toLowerCase();
  return rows.filter((row) => {
    if (!isCarried(row, jobIds)) return false;
    if (onlyDifferences && !differsSomewhere(row, jobIds)) return false;
    if (q === '') return true;
    if (row.key.toLowerCase().includes(q)) return true;
    // Every job's own spelling is searchable, not just the one the row is
    // titled with: all of them are real, and a PM types the one their own job
    // uses.
    return [row.label, ...Object.values(row.labels)].some((label) =>
      label.toLowerCase().includes(q),
    );
  });
}

/**
 * Why the table is empty — four different facts, and it reads wrong as any one
 * of the others: nothing on any roster, nothing on the jobs still ticked,
 * nothing that differs, or nothing matching what was typed. The
 * search-AND-differences case is its own sentence because "nothing matches
 * that search" is false when the search matched plenty and the toggle is what
 * emptied the table.
 *
 * `rowCount` is what the TICKED jobs carry (`carriedRows`), not the response:
 * with the roster of the one ticked job empty, a response-wide count would be
 * non-zero and the table would answer "nothing matches that search" to a
 * reader who never typed one. `jobsNarrowed` separates the two zero cases —
 * an empty response is "no roster anywhere", an emptied picker is not.
 */
export function emptyMessage({
  kind,
  rowCount,
  jobsNarrowed,
  onlyDifferences,
  searching,
}: {
  kind: RateComparisonKind;
  rowCount: number;
  jobsNarrowed?: boolean;
  onlyDifferences: boolean;
  searching: boolean;
}): string {
  if (rowCount === 0) {
    if (jobsNarrowed) {
      return kind === 'employees'
        ? 'No employees on the ticked jobs.'
        : 'No equipment on the ticked jobs.';
    }
    return kind === 'employees'
      ? 'No active employees on any of your jobs.'
      : 'No active equipment on any of your jobs.';
  }
  if (onlyDifferences && searching) {
    return 'Nothing that matches that search differs between jobs.';
  }
  if (onlyDifferences) return 'Every job that carries a row carries it at the same rate.';
  return 'Nothing matches that search.';
}

/**
 * The one muted line under the controls: how much of the roster is on screen.
 *
 * Stated once, here, and nowhere else (RULES.md 1.5) — this table has no
 * section title to hang a count off, and it is long enough that a reader
 * cannot otherwise tell whether a short list means a narrow filter or a short
 * roster. Unfiltered it names the roster; filtered it says how much of it
 * survived and WHICH control did the narrowing, because the two read
 * completely differently to someone deciding whether to trust the list.
 *
 * `total` is the roster of the TICKED jobs (see `carriedRows`), not of the
 * response — with two of eight jobs on screen, the six unticked rosters are
 * not part of what the reader is being told the size of.
 *
 * "equipment" is a mass noun, so it carries a unit at one rather than reading
 * "1 equipment".
 */
export function countsLine({
  total,
  shown,
  query,
  onlyDifferences,
  kind,
}: {
  total: number;
  shown: number;
  query: string;
  onlyDifferences: boolean;
  kind: RateComparisonKind;
}): string {
  const searching = query.trim() !== '';
  if (!searching && !onlyDifferences) {
    if (kind === 'employees') return `${total} ${total === 1 ? 'employee' : 'employees'}`;
    return total === 1 ? '1 piece of equipment' : `${total} equipment`;
  }
  const lead = `${shown} of ${total}`;
  const one = shown === 1;
  if (searching && onlyDifferences) {
    return `${lead} ${one ? 'matches' : 'match'} "${query.trim()}" and ${
      one ? 'differs' : 'differ'
    }`;
  }
  if (searching) return `${lead} ${one ? 'matches' : 'match'} "${query.trim()}"`;
  return `${lead} ${one ? 'differs' : 'differ'}`;
}

/** One of the two lines in the identity cell. `voice` is what it reads as —
 *  a name in the table's own cell voice, or an id in mono — and is separate
 *  from the rank it was given, so the styling never has to ask about kind. */
export interface KeyCellLine {
  text: string;
  voice: 'name' | 'id';
}

/**
 * The identity cell's two lines, in the order they are drawn, and THE ONLY
 * PLACE the order is decided.
 *
 * Employees lead with the NAME (client ruling 2026-09-22): the id had the big
 * line and the name the small muted one under it, and the name is what the
 * reader came to find. Equipment leads with the ASSET ID, deliberately, and
 * did not change with it — the PM running the equipment side reads those
 * codes faster than the descriptions, so promoting "2024 Chevy Silverado
 * 3500HD 4x4" over "FT214" would be demoting the thing he actually scans for.
 *
 * Both lines are always present either way. This decides rank, never content.
 */
export function keyCellLines(
  row: Pick<RateComparisonRow, 'key' | 'label'>,
  kind: RateComparisonKind,
): [KeyCellLine, KeyCellLine] {
  const id: KeyCellLine = { text: row.key, voice: 'id' };
  const name: KeyCellLine = { text: row.label, voice: 'name' };
  return kind === 'employees' ? [name, id] : [id, name];
}

/**
 * The jobs filter button's label: what it says while closed.
 *
 * Two shapes, because they answer two different questions. Untouched, the
 * reader needs to know the table is not hiding anything — "All jobs", not
 * "8 of 8 jobs", which invites a count nobody asked for. Narrowed, the count
 * IS the fact: the button is the only thing on screen that explains why there
 * are three columns, so it carries both halves of the ratio.
 */
export function jobsFilterLabel({ shown, total }: { shown: number; total: number }): string {
  if (shown === total) return 'All jobs';
  return `${shown} of ${total} jobs`;
}

/**
 * One cell. An em dash means the job does not carry the row at all — never
 * "$0.00", which would read as a rate somebody set.
 */
export function formatCell(cents: number | null): string {
  return cents === null ? '—' : formatMoneyCents(cents, { precise: true });
}
